import type { Node as ProseMirrorNode } from 'prosemirror-model';
import { WEBSITE_BUILDER_IMAGE_VIEWER_ENABLED_KEY } from './image-viewer.js';
import { createWebsiteComponentId, defaultWebsiteComponentAttrs, WEBSITE_COMPONENT_KEY } from './schema.js';
import type {
  WebsiteBuilderDocumentEnvelope,
  WebsiteBuilderNodeJSON,
  WebsiteComponentAttrs,
  WebsiteComponentDefinition,
  WebsiteDocumentValidationIssue,
} from './types.js';

export class WebsiteComponentRegistry {
  private readonly definitions = new Map<string, WebsiteComponentDefinition>();

  constructor(definitions: readonly WebsiteComponentDefinition[] = []) {
    definitions.forEach(definition => this.register(definition));
  }

  register(definition: WebsiteComponentDefinition): this {
    if (!definition.type.trim()) throw new Error('Website component types must not be empty.');
    if (this.definitions.has(definition.type)) {
      throw new Error(`Website component '${definition.type}' is already registered.`);
    }
    this.definitions.set(definition.type, withImageViewerInspector(definition));
    return this;
  }

  unregister(type: string): boolean {
    return this.definitions.delete(type);
  }

  get(type: string): WebsiteComponentDefinition | undefined {
    return this.definitions.get(type);
  }

  has(type: string): boolean {
    return this.definitions.has(type);
  }

  list(): WebsiteComponentDefinition[] {
    return [...this.definitions.values()];
  }

  categories(): Array<{ name: string; components: WebsiteComponentDefinition[] }> {
    const groups = new Map<string, WebsiteComponentDefinition[]>();
    for (const definition of this.definitions.values()) {
      const definitions = groups.get(definition.category) ?? [];
      definitions.push(definition);
      groups.set(definition.category, definitions);
    }
    return [...groups.entries()].map(([name, components]) => ({ name, components }));
  }

  createAttrs(type: string, overrides: Partial<WebsiteComponentAttrs> = {}): WebsiteComponentAttrs {
    const definition = this.get(type);
    if (!definition) throw new Error(`Website component '${type}' is not registered.`);
    return {
      ...defaultWebsiteComponentAttrs(),
      componentType: type,
      componentVersion: definition.version,
      props: structuredClone(definition.defaultProps ?? {}),
      styles: structuredClone(definition.defaultStyles ?? {}),
      responsive: structuredClone(definition.defaultResponsive ?? {}),
      ...overrides,
    };
  }

  createNodeJSON(type: string, overrides: Partial<WebsiteComponentAttrs> = {}): WebsiteBuilderNodeJSON {
    const definition = this.get(type);
    if (!definition) throw new Error(`Website component '${type}' is not registered.`);
    return {
      type: WEBSITE_COMPONENT_KEY,
      attrs: this.createAttrs(type, overrides) as unknown as Record<string, unknown>,
      ...(definition.defaultContent?.length ? { content: cloneDefaultContent(definition.defaultContent) } : {}),
    };
  }

  migrateAttrs(attrs: WebsiteComponentAttrs): WebsiteComponentAttrs {
    const definition = this.get(attrs.componentType);
    if (!definition || attrs.componentVersion >= definition.version) return attrs;
    const migrated = definition.migrate?.(attrs, attrs.componentVersion) ?? attrs;
    return { ...migrated, componentVersion: definition.version };
  }

  validateDocument(document: WebsiteBuilderDocumentEnvelope | ProseMirrorNode): WebsiteDocumentValidationIssue[] {
    const issues: WebsiteDocumentValidationIssue[] = [];

    const visitJSON = (node: WebsiteBuilderNodeJSON, parents: string[], path: number[]) => {
      if (node.type === WEBSITE_COMPONENT_KEY) {
        const attrs = node.attrs as Partial<WebsiteComponentAttrs> | undefined;
        this.validateAttrsAndRelation(attrs, parents.at(-1), path, issues);
        const type = typeof attrs?.componentType === 'string' ? attrs.componentType : 'unknown';
        node.content?.forEach((child, index) => visitJSON(child, [...parents, type], [...path, index]));
        return;
      }
      node.content?.forEach((child, index) => visitJSON(child, parents, [...path, index]));
    };

    if (isWebsiteDocumentEnvelope(document)) {
      visitJSON(document.doc, [], []);
      return issues;
    }

    const visitNode = (node: ProseMirrorNode, parents: string[], path: number[]) => {
      if (node.type.name === WEBSITE_COMPONENT_KEY) {
        const attrs = node.attrs as WebsiteComponentAttrs;
        this.validateAttrsAndRelation(attrs, parents.at(-1), path, issues);
        node.forEach((child, _offset, index) => visitNode(child, [...parents, attrs.componentType], [...path, index]));
        return;
      }
      node.forEach((child, _offset, index) => visitNode(child, parents, [...path, index]));
    };
    visitNode(document, [], []);
    return issues;
  }

  canContain(parentType: string | undefined, childType: string): boolean {
    const child = this.get(childType);
    if (!child) return true;
    if (parentType && child.allowedParents && !child.allowedParents.includes(parentType)) return false;
    const parent = parentType ? this.get(parentType) : undefined;
    if (parent?.allowedChildren && !parent.allowedChildren.includes(childType)) return false;
    return true;
  }

  private validateAttrsAndRelation(
    attrs: Partial<WebsiteComponentAttrs> | undefined,
    parentType: string | undefined,
    path: number[],
    issues: WebsiteDocumentValidationIssue[],
  ) {
    if (!attrs || typeof attrs.id !== 'string' || typeof attrs.componentType !== 'string') {
      issues.push({ code: 'invalid-attrs', message: 'Component attributes are incomplete.', path });
      return;
    }
    const definition = this.get(attrs.componentType);
    if (!definition) {
      issues.push({
        code: 'unknown-component',
        message: `Component '${attrs.componentType}' is not registered.`,
        componentId: attrs.id,
        componentType: attrs.componentType,
        path,
      });
      return;
    }
    if (!this.canContain(parentType, attrs.componentType)) {
      issues.push({
        code: 'invalid-parent',
        message: `Component '${attrs.componentType}' is not allowed inside '${parentType ?? 'document'}'.`,
        componentId: attrs.id,
        componentType: attrs.componentType,
        path,
      });
    }
    for (const message of definition.validate?.(attrs as WebsiteComponentAttrs) ?? []) {
      issues.push({
        code: 'invalid-attrs',
        message,
        componentId: attrs.id,
        componentType: attrs.componentType,
        path,
      });
    }
  }
}

const withImageViewerInspector = (definition: WebsiteComponentDefinition): WebsiteComponentDefinition => {
  const hasImage = definition.inspector?.some(field => field.kind === 'image');
  const hasViewerField = definition.inspector?.some(
    field => field.target === 'props' && field.key === WEBSITE_BUILDER_IMAGE_VIEWER_ENABLED_KEY,
  );
  if (!hasImage || hasViewerField) return definition;
  const enabledByDefault = Boolean(definition.imageViewer?.enabledByDefault);
  return {
    ...definition,
    defaultProps: {
      ...(definition.defaultProps ?? {}),
      [WEBSITE_BUILDER_IMAGE_VIEWER_ENABLED_KEY]: enabledByDefault,
    },
    inspector: [
      ...(definition.inspector ?? []),
      {
        key: WEBSITE_BUILDER_IMAGE_VIEWER_ENABLED_KEY,
        target: 'props',
        kind: 'boolean',
        label: 'Antippen zum Vergrößern',
        helpText:
          'Öffnet das Bild in einer zoombaren Vollbildansicht. Unterstützt Pinch-to-Zoom, Doppeltippen und Verschieben.',
        defaultValue: enabledByDefault,
      },
    ],
  };
};

const cloneDefaultContent = (content: readonly WebsiteBuilderNodeJSON[]): WebsiteBuilderNodeJSON[] =>
  structuredClone(content).map(renewNestedComponentIds);

const renewNestedComponentIds = (node: WebsiteBuilderNodeJSON): WebsiteBuilderNodeJSON => ({
  ...node,
  ...(node.type === WEBSITE_COMPONENT_KEY ? { attrs: { ...(node.attrs ?? {}), id: createWebsiteComponentId() } } : {}),
  ...(node.content ? { content: node.content.map(renewNestedComponentIds) } : {}),
});

const isWebsiteDocumentEnvelope = (
  value: WebsiteBuilderDocumentEnvelope | ProseMirrorNode,
): value is WebsiteBuilderDocumentEnvelope => 'format' in value && value.format === 'adornis.website';
